import React, { useEffect, useReducer } from 'react';
import { usePost, useGet } from 'http/useApi';
import { rq, rqPost } from 'http/apiRoutes';
import useWindowsDimension from 'functions/useWindowsDimension';
import {
  Grid,
  Box,
  List,
  ListItem,
  Button,
  Checkbox,
  ListItemText,
  Select,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import { useAtom } from 'jotai';
import userAtom from 'data/atoms/userAtom';
import { useTranslation } from 'react-i18next';
import { SearchFilter } from 'components/ui/SearchFilter';

import _ from 'lodash';

import {
  ICompanyViewModel,
  IDepartmentViewModel,
} from 'Interfaces/ICompaniesConfig';

import { IBrandViewModel } from 'pages/Setup/setupConfig';
import { IProductTypeViewModel } from 'pages/Billing/BillingConfig';
import {
  CloudFactoryAddProductListViewModel,
  CloudFactoryAddProductViewModel,
  CloudFactoryProductViewModel,
} from './Converts/ICloudFactoryInterfaces';
import { useSnackbar } from 'notistack';
export interface IImportProductsInterface {
  products: CloudFactoryProductViewModel[];
  selectedProducts: string[];
  productType: IProductTypeViewModel[];
  brands: IBrandViewModel[];
  productTypeMapping: { [key: string]: string };
  brandTypeMapping: { [key: string]: string };
}

const initialState: IImportProductsInterface = {
  products: [],
  selectedProducts: [],
  productType: [],
  brands: [],
  productTypeMapping: {},
  brandTypeMapping: {},
};

type Action =
  | { type: 'RESET_FORM' }
  | { type: 'CLEAR_SELECTIONS' }
  | { type: 'SET_PRODUCTS'; payload: CloudFactoryProductViewModel[] }
  | {
      type: 'UPDATE_PRODUCT_TYPE';
      payload: { key: string; productTypeId: string };
    }
  | { type: 'UPDATE_ALL_PRODUCT_TYPES'; payload: string }
  | { type: 'UPDATE_PRODUCT_BRAND'; payload: { key: string; brandId: string } }
  | { type: 'UPDATE_ALL_BRANDS'; payload: string }
  | { type: 'TOGGLE_PRODUCT_SELECTION'; payload: string }
  | { type: 'TOGGLE_ALL_PRODUCT_SELECTION' }
  | { type: 'SET_PRODUCTS_TYPE_LIST'; payload: IProductTypeViewModel[] }
  | { type: 'SET_BRANDS'; payload: IBrandViewModel[] };

const reducer = (
  state: IImportProductsInterface,
  action: Action
): IImportProductsInterface => {
  switch (action.type) {
    case 'RESET_FORM':
      return { ...initialState };
    case 'CLEAR_SELECTIONS':
      return {
        ...state,
        selectedProducts: [],
        productTypeMapping: {},
        brandTypeMapping: {},
      };
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload };
    case 'UPDATE_PRODUCT_TYPE':
      return {
        ...state,
        productTypeMapping: {
          ...state.productTypeMapping,
          [action.payload.key]: action.payload.productTypeId,
        },
      };
    case 'UPDATE_ALL_PRODUCT_TYPES':
      return {
        ...state,
        productTypeMapping: state.products.reduce<{ [key: string]: string }>(
          (acc, product) => {
            const key = `${product.product.name}-${product.product.bindingPeriod}-${product.product.paymentCycle}-${product.product.thirdPartyId}-${product.quantity}-${product.companyProductQuantity}`;
            acc[key] = action.payload;
            return acc;
          },
          {}
        ),
      };
    case 'UPDATE_PRODUCT_BRAND':
      return {
        ...state,
        brandTypeMapping: {
          ...state.brandTypeMapping,
          [action.payload.key]: action.payload.brandId,
        },
      };
    case 'UPDATE_ALL_BRANDS':
      return {
        ...state,
        brandTypeMapping: state.products.reduce<{ [key: string]: string }>(
          (acc, product) => {
            const key = `${product.product.name}-${product.product.bindingPeriod}-${product.product.paymentCycle}-${product.product.thirdPartyId}-${product.quantity}-${product.companyProductQuantity}`;
            acc[key] = action.payload;
            return acc;
          },
          {}
        ),
      };
    case 'TOGGLE_PRODUCT_SELECTION':
      return {
        ...state,
        selectedProducts: state.selectedProducts.includes(action.payload)
          ? state.selectedProducts.filter((key) => key !== action.payload)
          : [...state.selectedProducts, action.payload],
      };
    case 'TOGGLE_ALL_PRODUCT_SELECTION':
      const allSelected =
        state.selectedProducts.length === state.products.length;
      return {
        ...state,
        selectedProducts: allSelected
          ? []
          : state.products.map(
              (product) =>
                `${product.product.name}-${product.product.bindingPeriod}-${product.product.paymentCycle}-${product.product.thirdPartyId}-${product.quantity}-${product.companyProductQuantity}`
            ),
      };
    case 'SET_PRODUCTS_TYPE_LIST':
      return { ...state, productType: action.payload };
    case 'SET_BRANDS':
      return { ...state, brands: action.payload };
    default:
      return state;
  }
};

export const ImportProductList = ({
  expanded,
  products,
  departments,
  company,
  handleRemoveProducts,
}: {
  expanded: boolean;
  products: CloudFactoryProductViewModel[];
  departments: IDepartmentViewModel[];
  company: ICompanyViewModel | null;
  handleRemoveProducts: (products: CloudFactoryProductViewModel[]) => void;
}) => {
  const { t } = useTranslation();
  const { height } = useWindowsDimension();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [isSaving, setIsSaving] = React.useState(false);
  const [user] = useAtom(userAtom);
  const { enqueueSnackbar } = useSnackbar();

  const listHeight = height - 238;
  console.log('ImportProductList - products:', products);
  const postProducts = usePost(rqPost.importProducts(true), );
  const { data: ProductsTypesData } = useGet(rq.productTypes(true) );
  useEffect(() => {
    if (ProductsTypesData) {
      dispatch({
        type: 'SET_PRODUCTS_TYPE_LIST',
        payload: _.cloneDeep(ProductsTypesData.productType),
      });
    }
  }, [ProductsTypesData]);

  const { data: BrandData } = useGet(rq.getAllBrands(1, true), );
  useEffect(() => {
    if (BrandData) {
      dispatch({ type: 'SET_BRANDS', payload: _.cloneDeep(BrandData) });
    }
  }, [BrandData]);

  useEffect(() => {
    if (products.length > 0) {
      dispatch({ type: 'SET_PRODUCTS', payload: _.cloneDeep(products) });
      dispatch({ type: 'CLEAR_SELECTIONS' });
    } else {
      dispatch({ type: 'SET_PRODUCTS', payload: [] });
      dispatch({ type: 'CLEAR_SELECTIONS' });
    }
  }, [products]);

  const generateProductKey = (product: CloudFactoryProductViewModel) => {
    return `${product.product.name}-${product.product.bindingPeriod}-${product.product.paymentCycle}-${product.product.thirdPartyId}-${product.quantity}-${product.companyProductQuantity}`;
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    if (!company?.id) return;
    setIsSaving(true);
    const ProductsList: CloudFactoryAddProductViewModel[] = [];
    console.log('onSubmit - state.selectedProducts:', state.selectedProducts);

    state.selectedProducts.forEach((productKey) => {
      const product = state.products.find(
        (p: CloudFactoryProductViewModel) =>
          generateProductKey(p) === productKey
      );
      if (!product) return;
      const diff = product.quantity - (product.companyProductQuantity || 0);
      if (diff <= 0) return;

      ProductsList.push({
        product: {
          ...product.product,
          brandId:
            state.brandTypeMapping[productKey] || product.product.brandId || '',
          productType: {
            ...product.product.productType,
            id:
              state.productTypeMapping[productKey] ||
              product.product.productType?.id ||
              '',
          },
        },
        quantity: diff,
        createdBy: user.id,
        companyId: company.id,
      } as CloudFactoryAddProductViewModel);
    });
    console.log('onSubmit - ProductsList:', ProductsList);
    enqueueSnackbar(`Lagrer Vennligst vent!`, {
      variant: 'info',
    });
    postProducts.mutate(
      {
        NumberOfProducts: ProductsList.length,
        Products: ProductsList,
      },
      {
        onSuccess: (res: CloudFactoryAddProductListViewModel) => {
          // res.products er av typen CloudFactoryAddProductViewModel[]
          // Vi må transformere disse til CloudFactoryProductViewModel
          const imported: CloudFactoryProductViewModel[] = res.products.map(
            (p) => ({
              companyId: p.companyId,
              product: p.product, // allerede IProductViewModel
              quantity: p.quantity,
              companyProductQuantity: 0,
            })
          );

          // Kall handleRemoveProducts med arrayen, ikke et objekt
          handleRemoveProducts(imported);

          enqueueSnackbar(`Lagret`, { variant: 'success' });
          console.log('onSubmit - Products imported successfully:', res);
        },
      }
    );
  };

  // check if there ar products at all
  if (!products || products.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant='body1'>
          No products available to import.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid
      container
      sx={{
        m: 0,
        p: 0,
      }}
    >
      {/* HEADER */}
      <Grid item xs={12}>
        <SearchFilter
          Icon={FeedRoundedIcon}
          Header={t('Products.ProductForm.productDetails')}
          filterValue={null}
          handleFilterChange={null}
          handleSearch={null}
          handleNew={null}
          selectList={null}
          gfx={
            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              <Stack direction='column' spacing={0} sx={{ pr: 4 }}>
                <Typography variant='caption' color='primary.text'>
                  {t('Settings.Brand.header')}
                </Typography>
                <Select
                  size='small'
                  variant='standard'
                  value=''
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_ALL_BRANDS',
                      payload: e.target.value as string,
                    })
                  }
                  displayEmpty
                  sx={{ minWidth: 170, color: 'white' }}
                >
                  {state.brands &&
                    state.brands.map((Brand) => (
                      <MenuItem key={Brand.id} value={Brand.id}>
                        {Brand.brandName}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>
              <Stack direction='column' spacing={0} sx={{ pr: 2 }}>
                <Typography variant='caption' color='primary.text'>
                  {t('Products.ProductForm.productType')}
                </Typography>
                <Select
                  size='small'
                  variant='standard'
                  value=''
                  onChange={(e) =>
                    dispatch({
                      type: 'UPDATE_ALL_PRODUCT_TYPES',
                      payload: e.target.value as string,
                    })
                  }
                  displayEmpty
                  sx={{ minWidth: 170, color: 'white' }}
                >
                  {state.productType &&
                    state.productType.map((ProductsType) => (
                      <MenuItem key={ProductsType.id} value={ProductsType.id}>
                        {ProductsType.name}
                      </MenuItem>
                    ))}
                </Select>
              </Stack>
              <Checkbox
                sx={{ pr: 4 }}
                checked={
                  state.selectedProducts.length === state.products.length
                }
                color={
                  state.selectedProducts.length === state.products.length
                    ? 'secondary'
                    : 'primary'
                }
                onChange={() =>
                  dispatch({
                    type: 'TOGGLE_ALL_PRODUCT_SELECTION',
                  })
                }
              />
            </Box>
          }
        />
      </Grid>

      <List
        sx={{
          width: '100%',
          height: listHeight,
          p: 1,
          borderRadius: 0,
          overflow: 'auto',
          backgroundColor: 'primary.main',
        }}
      >
        {state.products?.length > 0 &&
          state.productType.length > 0 &&
          state.brands.length > 0 &&
          state.products.map(
            (product: CloudFactoryProductViewModel, index: number) => {
              const productKey = generateProductKey(product);
              const diff =
                product.quantity - (product.companyProductQuantity || 0);
              console.log('diff:', diff, product);
              // If
              if (diff === 0) return null;

              return (
                <ListItem
                  key={productKey}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor:
                      diff < 0
                        ? 'warning.dark'
                        : diff > 0
                        ? 'success.dark'
                        : 'transparent',
                    borderRadius: 3,
                    boxShadow: 2,
                    mb: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <ListItemText
                      primary={product.product.name}
                      secondary={`${product.product.paymentCycle} / ${
                        product.product.bindingPeriod
                      }  (${
                        diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : ''
                      }) --- ${
                        product.product.retailPrice
                          ? `price: ${product.product.retailPrice}`
                          : ''
                      }`}
                      primaryTypographyProps={{ color: 'white' }}
                      secondaryTypographyProps={{ color: 'white' }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 2,
                      width: '10%',
                    }}
                  >
                    <Stack direction='column' spacing={0}>
                      <Typography variant='caption' color='primary.text'>
                        {t('modules.cloudFactory.subscriptions')}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='primary.text'
                        sx={{
                          color:
                            product.companyProductQuantity < 0
                              ? 'red'
                              : product.companyProductQuantity === 0
                              ? 'white'
                              : 'lightGreen',
                          fontSize: 22,
                          fontWeight: 'bold',
                          alignSelf: 'center',
                        }}
                      >
                        {product.companyProductQuantity || 0}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 2,
                      width: '10%',
                    }}
                  >
                    <Stack direction='column' spacing={0}>
                      <Typography variant='caption' color='primary.text'>
                        {t('modules.cloudFactory.newSubscriptions')}
                      </Typography>
                      <Typography
                        variant='caption'
                        color='primary.text'
                        sx={{
                          color:
                            diff < 0
                              ? 'Red'
                              : diff === 0
                              ? 'white'
                              : 'lightGreen',
                          fontSize: 22,
                          fontWeight: 'bold',
                          alignSelf: 'center',
                        }}
                      >
                        {diff}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 2,
                    }}
                  >
                    <Stack direction='column' spacing={0}>
                      <Typography variant='caption' color='primary.text'>
                        {t('Settings.Brand.header')}
                      </Typography>
                      <Select
                        size='small'
                        variant='standard'
                        value={
                          state.brandTypeMapping[productKey] ||
                          product.product.brandId ||
                          ''
                        }
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_PRODUCT_BRAND',
                            payload: {
                              key: productKey,
                              brandId: e.target.value as string,
                            },
                          })
                        }
                        displayEmpty
                        sx={{ minWidth: 170, color: 'white' }}
                      >
                        {state.brands &&
                          state.brands.map((Brand) => (
                            <MenuItem key={Brand.id} value={Brand.id}>
                              {Brand.brandName}
                            </MenuItem>
                          ))}
                      </Select>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 2,
                    }}
                  >
                    <Stack direction='column' spacing={0}>
                      <Typography variant='caption' color='primary.text'>
                        {t('Products.ProductForm.productType')}
                      </Typography>
                      <Select
                        size='small'
                        variant='standard'
                        value={
                          state.productTypeMapping[productKey] ||
                          product.product.productType?.name ||
                          ''
                        }
                        onChange={(e) =>
                          dispatch({
                            type: 'UPDATE_PRODUCT_TYPE',
                            payload: {
                              key: productKey,
                              productTypeId: e.target.value as string,
                            },
                          })
                        }
                        displayEmpty
                        sx={{ minWidth: 170, color: 'white' }}
                      >
                        {state.productType &&
                          state.productType.map((ProductsType) => (
                            <MenuItem
                              key={ProductsType.id}
                              value={ProductsType.id}
                            >
                              {ProductsType.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </Stack>
                  </Box>

                  <Checkbox
                    checked={state.selectedProducts.includes(productKey)}
                    color={
                      state.selectedProducts.includes(productKey)
                        ? 'secondary'
                        : 'primary'
                    }
                    onChange={() =>
                      dispatch({
                        type: 'TOGGLE_PRODUCT_SELECTION',
                        payload: productKey,
                      })
                    }
                  />
                </ListItem>
              );
            }
          )}
      </List>
      {/*   Submit Button */}

      <Grid
        item
        xs={12}
        sx={{
          m: 0,
          p: 1,
          height: 64,
          display: 'flex',
          flexDirection: 'row-reverse',
          backgroundColor: 'primary.main',
          borderRadius: 2,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        <Button
          disabled={isSaving || postProducts.isLoading}
          onClick={(e) => {
            onSubmit(e);
          }}
          variant='contained'
        >
          {postProducts.isLoading ? t('button.creating') : t('button.create')}
        </Button>
      </Grid>
    </Grid>
  );
};
